import React, { createContext, useState, useContext, useCallback } from 'react';
import axios from 'axios';

const PropiedadesContext = createContext();

export const PropiedadesProvider = ({ children }) => {
  const [propiedades, setPropiedades] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [selectedProyecto, setSelectedProyecto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchPropiedades = useCallback(async (filters = {}) => {
    // Limpiar filtros y extraer información especial de ubicación
    const {
      ubicacionTipo, 
      ubicacionValor, 
      ...otherFilters
    } = Object.fromEntries(
      Object.entries(filters)
        .filter(([_, value]) => value != null && value !== '')
        .map(([key, value]) => {
          if (key === 'ubicacion' && value.includes('(')) {
            // Si la ubicación incluye un tipo entre paréntesis, extraer solo el nombre
            return [key, value.split(' (')[0]];
          }
          return [key, typeof value === 'string' ? value.trim() : value];
        })
    );

    // Determinar si es alquiler o venta
    const isAlquiler = otherFilters.operacion === 'alquiler' || otherFilters.operacion === 'alquilar';

    try {
      setLoading(true);
      setError(null);

      console.log('Searching properties with filters:', otherFilters);
      
      // Si se especificó un tipo y valor de ubicación, añadirlos a los parámetros
      const searchParams = { ...otherFilters };
      if (ubicacionTipo && ubicacionValor) {
        searchParams.ubicacionTipo = ubicacionTipo;
        searchParams.ubicacionValor = ubicacionValor;
      }

      // Usar el endpoint correcto según el tipo de operación y si se especifica "filtrar"
      const endpoint = isAlquiler 
        ? 'https://backend-production-1786.up.railway.app/api/alquileres/filtrar'
        : 'https://backend-production-1786.up.railway.app/pi/propiedades/filtrar';

      // Prepare request configuration
      const config = {
        method: 'get',
        url: endpoint,
        params: searchParams,
        headers: {
          'Content-Type': 'application/json'
        }
      };

      // Add authentication token if available
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Make the API call
      const response = await axios(config);

      console.log('API Response:', response.data);

      // Format the received data
      const formattedProperties = response.data.map(property => ({
        ...property,
        precio: isAlquiler ? property.precio_mensual : property.precio_soles,
        tipo_precio: isAlquiler ? 'Alquiler (S/. mes)' : 'Venta (S/.)',
        operacion: isAlquiler ? 'alquiler' : 'venta',
        imagenes: Array.isArray(property.imagenes) 
          ? property.imagenes 
          : (property.imagenes ? property.imagenes.split(',') : []),
        vendedor_imagen: property.vendedor_imagen 
          ? (property.vendedor_imagen.startsWith('/') 
              ? property.vendedor_imagen 
              : `/profiles/${property.vendedor_imagen}`)
          : null
      }));

      setPropiedades(formattedProperties);
      return formattedProperties;

    } catch (error) {
      // En caso de error 404, intentamos con el endpoint sin "/filtrar"
      if (error.response && error.response.status === 404) {
        try {
          // Crear nuevos objetos para evitar referencias cruzadas con el ámbito anterior
          const fallbackSearchParams = { ...otherFilters };
          if (ubicacionTipo && ubicacionValor) {
            fallbackSearchParams.ubicacionTipo = ubicacionTipo;
            fallbackSearchParams.ubicacionValor = ubicacionValor;
          }
          
          // Intentar con el endpoint base
          const baseEndpoint = isAlquiler 
            ? 'https://backend-production-1786.up.railway.app/api/alquileres'
            : 'https://backend-production-1786.up.railway.app/api/propiedades';
          
          const fallbackConfig = {
            method: 'get',
            url: baseEndpoint,
            params: fallbackSearchParams,
            headers: {
              'Content-Type': 'application/json'
            }
          };
          
          // Add authentication token if available
          const token = localStorage.getItem('token');
          if (token) {
            fallbackConfig.headers['Authorization'] = `Bearer ${token}`;
          }
          
          const fallbackResponse = await axios(fallbackConfig);
          
          console.log('Fallback API Response:', fallbackResponse.data);
          
          // Format the received data
          const formattedProperties = fallbackResponse.data.map(property => ({
            ...property,
            precio: isAlquiler ? property.precio_mensual : property.precio_soles,
            tipo_precio: isAlquiler ? 'Alquiler (S/. mes)' : 'Venta (S/.)',
            operacion: isAlquiler ? 'alquiler' : 'venta',
            imagenes: Array.isArray(property.imagenes) 
              ? property.imagenes 
              : (property.imagenes ? property.imagenes.split(',') : []),
            vendedor_imagen: property.vendedor_imagen 
              ? (property.vendedor_imagen.startsWith('/') 
                  ? property.vendedor_imagen 
                  : `/profiles/${property.vendedor_imagen}`)
              : null
          }));
          
          setPropiedades(formattedProperties);
          setLoading(false);
          return formattedProperties;
        } catch (fallbackError) {
          console.error('Fallback Property Search Error:', {
            message: fallbackError.message,
            response: fallbackError.response?.data,
            status: fallbackError.response?.status,
            filters: filters
          });
          throw fallbackError;
        }
      }
      
      // Comprehensive error handling for original error
      console.error('Property Search Error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        filters: filters
      });

      // Set user-friendly error message
      const errorMessage = error.response?.data?.error 
        || error.message 
        || 'Error al buscar propiedades';

      setError(errorMessage);
      setPropiedades([]); // Clear properties on error

      throw error; // Re-throw to allow further error handling
    } finally {
      setLoading(false);
    }
  }, []);

  // Función específica para buscar alquileres
  const searchAlquileres = useCallback(async (filters = {}) => {
    // Asegurarse de que la operación se establece como alquiler
    return searchPropiedades({
      ...filters,
      operacion: 'alquilar'
    });
  }, [searchPropiedades]);

  // Nueva función para buscar proyectos inmobiliarios
  const searchProyectos = useCallback(async (filters = {}) => {
    // Limpiar filtros eliminando valores nulos o vacíos
    const cleanFilters = Object.fromEntries(
      Object.entries(filters)
        .filter(([_, value]) => value != null && value !== '')
        .map(([key, value]) => {
          if (typeof value === 'string') {
            return [key, value.trim()];
          }
          return [key, value];
        })
    );

    try {
      setLoading(true);
      setError(null);

      console.log('Searching projects with filters:', cleanFilters);

      // Configurar el endpoint para buscar proyectos
      const endpoint = 'https://backend-production-1786.up.railway.app/api/proyectos/filtrar';
      
      // Prepare request configuration
      const config = {
        method: 'get',
        url: endpoint,
        params: cleanFilters,
        headers: {
          'Content-Type': 'application/json'
        }
      };

      // Add authentication token if available
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Make the API call
      const response = await axios(config);

      console.log('API Response (Proyectos):', response.data);

      // Formato para los datos de proyectos recibidos
      const formattedProjects = response.data.map(project => ({
        ...project,
        // Determinar el rango de precios para mostrar (usando el menor precio disponible)
        precio_desde: getPrecioDesdeProyecto(project),
        tipo_precio: 'Desde (S/.)',
        // Procesar las imágenes
        imagenes: Array.isArray(project.imagenes) 
          ? project.imagenes 
          : (project.imagenes ? project.imagenes.split(',') : []),
        // Procesar campos JSON
        bancos_financiamiento: typeof project.bancos_financiamiento === 'string' 
          ? JSON.parse(project.bancos_financiamiento) 
          : (project.bancos_financiamiento || []),
        amenidades: typeof project.amenidades === 'string' 
          ? JSON.parse(project.amenidades) 
          : (project.amenidades || []),
        // Procesar la imagen del vendedor
        vendedor_imagen: project.vendedor_foto_perfil 
          ? `/profiles/${project.vendedor_foto_perfil}` 
          : null
      }));

      setProyectos(formattedProjects);
      return formattedProjects;

    } catch (error) {
      // En caso de error 404, intentar con el endpoint general
      if (error.response && error.response.status === 404) {
        try {
          // Intentar con el endpoint base
          const baseEndpoint = 'https://backend-production-1786.up.railway.app/api/proyectos';
          
          const fallbackConfig = {
            method: 'get',
            url: baseEndpoint,
            params: cleanFilters,
            headers: {
              'Content-Type': 'application/json'
            }
          };
          
          // Add authentication token if available
          const token = localStorage.getItem('token');
          if (token) {
            fallbackConfig.headers['Authorization'] = `Bearer ${token}`;
          }
          
          const fallbackResponse = await axios(fallbackConfig);
          
          console.log('Fallback API Response (Proyectos):', fallbackResponse.data);
          
          // Formato para los datos de proyectos recibidos
          const formattedProjects = fallbackResponse.data.map(project => ({
            ...project,
            // Determinar el rango de precios para mostrar (usando el menor precio disponible)
            precio_desde: getPrecioDesdeProyecto(project),
            tipo_precio: 'Desde (S/.)',
            // Procesar las imágenes
            imagenes: Array.isArray(project.imagenes) 
              ? project.imagenes 
              : (project.imagenes ? project.imagenes.split(',') : []),
            // Procesar campos JSON
            bancos_financiamiento: typeof project.bancos_financiamiento === 'string' 
              ? JSON.parse(project.bancos_financiamiento) 
              : (project.bancos_financiamiento || []),
            amenidades: typeof project.amenidades === 'string' 
              ? JSON.parse(project.amenidades) 
              : (project.amenidades || []),
            // Procesar la imagen del vendedor
            vendedor_imagen: project.vendedor_foto_perfil 
              ? `/profiles/${project.vendedor_foto_perfil}` 
              : null
          }));
          
          setProyectos(formattedProjects);
          setLoading(false);
          return formattedProjects;
        } catch (fallbackError) {
          console.error('Fallback Project Search Error:', {
            message: fallbackError.message,
            response: fallbackError.response?.data,
            status: fallbackError.response?.status,
            filters: filters
          });
          throw fallbackError;
        }
      }
      
      console.error('Project Search Error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        filters: filters
      });

      // Set user-friendly error message
      const errorMessage = error.response?.data?.error 
        || error.message 
        || 'Error al buscar proyectos inmobiliarios';

      setError(errorMessage);
      setProyectos([]); // Clear projects on error

      throw error; // Re-throw to allow further error handling
    } finally {
      setLoading(false);
    }
  }, []);

  // Función para obtener un proyecto específico por ID
  const getProyectoById = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching project with ID:', id);
      
      // Configurar el endpoint para buscar un proyecto por ID
      const endpoint = `https://backend-production-1786.up.railway.app/api/proyectos/${id}`;
      
      // Prepare request configuration
      const config = {
        method: 'get',
        url: endpoint,
        headers: {
          'Content-Type': 'application/json'
        }
      };

      // Add authentication token if available
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Make the API call
      const response = await axios(config);

      console.log('API Response (Proyecto):', response.data);

      // El proyecto ya viene formateado desde el API
      const proyecto = response.data;
      
      // Establecer el proyecto seleccionado
      setSelectedProyecto(proyecto);
      return proyecto;

    } catch (error) {
      console.error('Get Project Error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        id: id
      });

      // Set user-friendly error message
      const errorMessage = error.response?.data?.error 
        || error.message 
        || 'Error al obtener el proyecto inmobiliario';

      setError(errorMessage);
      setSelectedProyecto(null);

      throw error; // Re-throw to allow further error handling
    } finally {
      setLoading(false);
    }
  }, []);

  // Función auxiliar para determinar el precio mínimo de un proyecto
  const getPrecioDesdeProyecto = (proyecto) => {
    const precios = [];
    
    if (proyecto.tiene_1dorm && proyecto.precio_1dorm_soles > 0) {
      precios.push(proyecto.precio_1dorm_soles);
    }
    
    if (proyecto.tiene_2dorm && proyecto.precio_2dorm_soles > 0) {
      precios.push(proyecto.precio_2dorm_soles);
    }
    
    if (proyecto.tiene_3dorm && proyecto.precio_3dorm_soles > 0) {
      precios.push(proyecto.precio_3dorm_soles);
    }
    
    if (proyecto.tiene_4dorm && proyecto.precio_4dorm_soles > 0) {
      precios.push(proyecto.precio_4dorm_soles);
    }
    
    // Si no hay precios disponibles, devolver 0
    return precios.length > 0 ? Math.min(...precios) : 0;
  };

  // Reset properties and error state
  const resetPropiedades = useCallback(() => {
    setPropiedades([]);
    setError(null);
  }, []);

  // Reset projects and error state
  const resetProyectos = useCallback(() => {
    setProyectos([]);
    setSelectedProyecto(null);
    setError(null);
  }, []);

  // Context value with all necessary methods and states
  const value = {
    propiedades,
    proyectos,
    selectedProyecto,
    loading,
    error,
    searchPropiedades,
    searchAlquileres,
    searchProyectos,
    getProyectoById,
    resetPropiedades,
    resetProyectos,
    setPropiedades,
    setProyectos
  };

  return (
    <PropiedadesContext.Provider value={value}>
      {children}
    </PropiedadesContext.Provider>
  );
};

export const usePropiedades = () => {
  const context = useContext(PropiedadesContext);
  if (!context) {
    throw new Error('usePropiedades must be used within a PropiedadesProvider');
  }
  return context;
};